/**
 * Como a abertura nomeia cada CCB do envio e o que fica pendente nela (E144.F4, GUS-105 + GUS-135,
 * ADR-0098 §2.2–2.4). Regra pura, sem JSX.
 *
 * - **Rótulo**: a chave lida (ADE, número da cédula, proposta…) com o nome da fonte; sem chave, o
 *   nome do arquivo. A CCB declarada (E177.F5, ADR-0130) é "CCB <número>": o número que a pessoa
 *   informou, não o lido.
 * - **Material identificado**: quantos arquivos vão com a CCB e quantos deles são de apoio. A
 *   composição (biometria, fotos) é do caso e só existe depois do desdobramento.
 * - **Pendência curta**: o arquivo com mais de uma CCB, que a operação separa. O caso que já tem a
 *   CCB (complemento, já recebido, do escritório) aparece no destino da tabela (E165.F1.T05), e a
 *   linha embaixo diz, sem CCB que vá a caso novo, que nenhum caso novo será aberto — só depois da
 *   escolha de vincular, quando a CCB pede escolha (E177.F2, GUS-253 A2).
 */
import { ptBR } from '@/i18n/pt-BR';
import type {
  CasoEncontradoData,
  ContratoDoEnvioData,
  IdentificadorDoContratoData,
} from '@/lib/abertura_do_caso';
import type { CasoDoEnvioData } from '@/lib/conclusao_da_abertura';

import { podeIrACasoNovo, type EscolhasDoEnvio } from './escolhaDaCcb';

const t = ptBR.abertura.conferirEnvio;

export function rotuloDoContrato(contrato: {
  identificador: IdentificadorDoContratoData | null;
  nome: string;
  chave?: string;
}): string {
  // A chave `ccb:<id>` é a da CCB declarada, e o nome é o número informado (ADR-0130 §2.4).
  if (contrato.chave?.startsWith('ccb:') === true) {
    return ptBR.abertura.contratosDigitais.rotulo.replace('{numero}', contrato.nome);
  }
  const { identificador } = contrato;
  return identificador === null
    ? contrato.nome
    : `${t.fonte[identificador.fonte]} ${identificador.valor}`;
}

export function materialDoContrato(contrato: ContratoDoEnvioData): string {
  const arquivos = contrato.unidades.length + contrato.apoios.length;
  return contrato.apoios.length === 0
    ? t.material.replace('{n}', String(arquivos))
    : t.materialComApoio
        .replace('{n}', String(arquivos))
        .replace('{m}', String(contrato.apoios.length));
}

/** E163.F3 (A67-15): o caso que já tem a CCB saiu da linha e vai ao aviso "Caso encontrado". */
export function pendenciasDoContrato(contrato: ContratoDoEnvioData): string[] {
  return contrato.separacao_reprovada ? [t.pendencia.separacao] : [];
}

/** O título da tabela: quantas CCBs o envio identificou. */
export function tituloDosContratos(contratos: readonly ContratoDoEnvioData[]): string {
  const identificados = contratos.filter((c) => c.identificado);
  if (identificados.length === 0) return t.semContrato;
  return identificados.length === 1
    ? t.identificadaUma
    : t.identificadas.replace('{n}', String(identificados.length));
}

/**
 * E165.F1.T05 (GUS-105, HTML 6.8): o destino da CCB na tabela do Conferir — "Novo caso" ou o caso
 * que já a tem. O que se escolhe para a CCB que já tem caso (vincular ou abrir outro) é da T06.
 */
export function destinoDoContrato(contrato: ContratoDoEnvioData): string {
  const caso = casoQueJaTemACcb(contrato);
  if (caso === null) return t.destino.novo;
  // E165.F1.T06 (HTML 6.8): o caso que já tem a CCB diz se está aberto ou fechado.
  const situacao =
    contrato.situacao === 'complemento' && caso.fechado === true
      ? 'complementoFechado'
      : (contrato.situacao as Exclude<ContratoDoEnvioData['situacao'], 'novo'>);
  return t.destino[situacao].replace('{codigo}', caso.codigo);
}

/**
 * A linha embaixo da tabela: uma CCB por caso e por exame (como no HTML 6.8, também enquanto a
 * escolha da CCB que já tem caso está pendente); sem CCB que possa ir a caso novo (E165.F1.T06),
 * nenhum caso novo.
 */
export function resumoDosContratos(
  contratos: readonly ContratoDoEnvioData[],
  escolhas: EscolhasDoEnvio = {},
): string {
  if (contratos.length > 0 && !contratos.some((c) => podeIrACasoNovo(c, escolhas))) {
    return t.casoEncontrado.nenhumNovo;
  }
  return contratos.length === 1
    ? t.resumoUma
    : t.resumoVarias.replace('{n}', String(contratos.length));
}

/** O caso que já tem a CCB, quando ela não abre caso novo — o "Abrir o caso" do aviso. */
export function casoQueJaTemACcb(contrato: ContratoDoEnvioData): CasoEncontradoData | null {
  return contrato.situacao === 'novo' ? null : contrato.caso;
}

/**
 * E165.F1.T07 (HTML 6.8, ADR-0114 §4): o que a linha do Acompanhar diz depois do exame — a nova
 * análise com o caso de origem, o vínculo ao caso existente, a CCB já recebida ou o caso do
 * escritório. O caso novo sem origem não diz nada a mais: o código já é o do caso aberto.
 */
export function complementoDaLinha(caso: CasoDoEnvioData): string | null {
  const ta = ptBR.abertura.acompanhar.linha;
  if (caso.origem != null) return ta.novaAnalise.replace('{codigo}', caso.origem.codigo);
  return caso.situacao === 'novo' ? null : ta[caso.situacao];
}

/**
 * E165.F1.T03 (HTML 6.8): o rótulo acima do título diz quem abre — "Banco / Abertura" ou
 * "Escritório / Abertura"; a abertura interna segue com o rótulo de antes.
 */
export function eyebrowDaAbertura({
  doBanco,
  doCliente,
}: {
  doBanco: boolean;
  doCliente: boolean;
}): string {
  const ta = ptBR.abertura;
  if (doBanco) return ta.eyebrowBanco;
  return doCliente ? ta.eyebrowEscritorio : ta.eyebrow;
}
